import { Column } from "react-table-8.10.7";
import {
  RecognitionWaterfallAmounts,
  RecognitionWaterfallParty,
  RecognitionWaterfallPeriod,
} from "store/apis/recognitionWaterfall";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";
import { pluralize } from "utils/pluralize";
import { UNKNOWN_PARTY_ROW_ID, WaterfallLabels } from "./constants";

export const ROW_TYPE = { PARTY: "party", TOTAL: "total" } as const;

export const COLUMN_KIND = {
  PRIOR: "prior",
  PERIOD: "period",
  DEFERRED: "deferred",
} as const;

type RowBase = {
  id: string;
  label: string;
  amounts: RecognitionWaterfallAmounts;
};

export type WaterfallRow =
  // The party is kept so a clicked cell can list the invoices and items behind it
  | (RowBase & {
      type: typeof ROW_TYPE.PARTY;
      sublabel: string;
      party: RecognitionWaterfallParty;
    })
  | (RowBase & { type: typeof ROW_TYPE.TOTAL });

// The amount columns a cell can be clicked in
export type WaterfallColumn =
  | { kind: typeof COLUMN_KIND.PRIOR }
  | { kind: typeof COLUMN_KIND.PERIOD; index: number }
  | { kind: typeof COLUMN_KIND.DEFERRED };

const PINNED_Z_INDEX = 2;

// The same color for pinned and scrolling cells keeps a row one color without !important overrides
export const pinningStyles = (column: Column<WaterfallRow>, rowColor: string) =>
  getCommonPinningStyles(column, rowColor, rowColor, PINNED_Z_INDEX);

export const getColumnAmount = (
  amounts: RecognitionWaterfallAmounts,
  column: WaterfallColumn
): number => {
  if (column.kind === COLUMN_KIND.PRIOR) {
    return Number(amounts.prior_recognized);
  }
  if (column.kind === COLUMN_KIND.DEFERRED) return Number(amounts.deferred);
  return Number(amounts.periodic[column.index]);
};

// The column's header text, e.g. "Before Jan 2026", "Feb 2026" or "After Dec 2026"
export const getColumnTitle = (
  column: WaterfallColumn,
  periods: RecognitionWaterfallPeriod[]
): string => {
  if (column.kind === COLUMN_KIND.PRIOR) return `Before ${periods[0]?.period}`;
  if (column.kind === COLUMN_KIND.DEFERRED) {
    return `After ${periods.at(-1)?.period}`;
  }
  return periods[column.index]?.period;
};

// The same column in a sentence, e.g. "before Jan 2026" or "in Feb 2026"
export const getColumnLabel = (
  column: WaterfallColumn,
  periods: RecognitionWaterfallPeriod[]
): string => {
  if (column.kind === COLUMN_KIND.PERIOD) {
    return `in ${periods[column.index]?.period}`;
  }
  const title = getColumnTitle(column, periods);
  return title.charAt(0).toLowerCase() + title.slice(1);
};

export const toRows = (
  parties: RecognitionWaterfallParty[],
  labels: WaterfallLabels
): WaterfallRow[] =>
  parties.map((party) => ({
    type: ROW_TYPE.PARTY,
    id: party.party_id || UNKNOWN_PARTY_ROW_ID,
    label: party.party_name,
    sublabel: pluralize(
      party.documents.length,
      labels.documentSingular,
      labels.documentPlural
    ),
    amounts: party,
    party,
  }));
